import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { alpha, useTheme } from "@mui/material/styles";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// =======================================================
// COMMON HELPERS
// =======================================================

const toNumber = (value) => Number(value || 0);

const isManHourType = (type) => type === "Man" || type === "Man-Hour";

const roundSmallValues = (hours) => {
  const numericHours = Number(hours || 0);
  return numericHours < 0.01 ? 0.0 : Number(numericHours.toFixed(2));
};

const convertTimeToSeconds = (timeValue) => {
  if (!timeValue) return 0;

  if (typeof timeValue === "number") {
    return timeValue * 3600;
  }

  const text = String(timeValue);

  if (!text.includes(":")) {
    return Number(text || 0) * 3600;
  }

  const [hours = 0, minutes = 0, seconds = 0] = text.split(":").map(Number);
  return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
};

const convertTimeToDecimalHours = (timeValue) => {
  if (!timeValue) return 0;

  if (typeof timeValue === "number") {
    return Number(timeValue.toFixed(2));
  }

  const text = String(timeValue);

  if (!text.includes(":")) {
    return Number(Number(text || 0).toFixed(2));
  }

  return Number((convertTimeToSeconds(text) / 3600).toFixed(2));
};

const formatDecimalHours = (value) => {
  const numericValue = Number(value || 0);
  return numericValue === 0 ? 0 : Number(numericValue.toFixed(2));
};

const formatEmployees = (operator, astOperator, helpers) => {
  const employeesList = [];

  if (operator) {
    employeesList.push(...String(operator).split(",").map((name) => `${name.trim()}[OP]`));
  }

  if (astOperator) {
    employeesList.push(...String(astOperator).split(",").map((name) => `${name.trim()}[AOP]`));
  }

  if (helpers) {
    employeesList.push(...String(helpers).split(",").map((name) => `${name.trim()}[HLP]`));
  }

  return employeesList.filter(Boolean).join(", ");
};

const formatTableValue = (value, columnName = "") => {
  if (value === null || value === undefined) return "";

  const dateColumns = [
    "shift_loggedIn_DateTime",
    "shift_loggedOut_DateTime",
    "start_datetime",
    "end_datetime",
    "shift_in_datetime",
    "shift_out_datetime",
    "filter_start_datetime",
    "filter_end_datetime",
    "createdat",
    "lastupdate",
    "created_at",
    "updated_at",
  ];

  if (dateColumns.includes(columnName) && dayjs(value).isValid()) {
    return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
  }

  return String(value);
};

const formatNonJobActivities = (activitiesString) => {
  if (!activitiesString) return "";

  const activities = String(activitiesString)
    .split(",")
    .map((activity) => activity.trim())
    .filter(Boolean);

  return activities
    .map((activity) => {
      const match = activity.match(
        /^(.*?)\s+(\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}\s+[AP]M)-(\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}\s+[AP]M)$/
      );

      if (!match) return activity;

      const [, description, startDateTime, endDateTime] = match;
      const startDate = startDateTime.split(" ")[0];
      const startTime = startDateTime.split(" ").slice(1).join(" ");
      const endDate = endDateTime.split(" ")[0];
      const endTime = endDateTime.split(" ").slice(1).join(" ");

      if (startDate === endDate) {
        if (startTime === endTime) {
          return `${description} ${startDate} (${startTime})`;
        }
        return `${description} ${startDate} (${startTime} to ${endTime})`;
      }

      return `${description} ${startDate} ${startTime} to ${endDate} ${endTime}`;
    })
    .join("\n");
};

const processEmployeeActivitiesData = (employeeActivitiesData = []) => {
  return employeeActivitiesData.map((employee, index) => ({
    "No:": index + 1,
    emp_id: employee.emp_id,
    emp_name: employee.emp_name,
    shift_in_datetime: employee.shift_in_datetime
      ? dayjs(employee.shift_in_datetime).format("YYYY-MM-DD hh:mm A")
      : "",
    shift_out_datetime: employee.shift_out_datetime
      ? dayjs(employee.shift_out_datetime).format("YYYY-MM-DD hh:mm A")
      : "",
    non_job_activities: formatNonJobActivities(employee.non_job_activities || ""),
    remarks: employee.shift_remarks || "",
  }));
};

const getPlanColumns = (hoursType) => {
  if (isManHourType(hoursType)) {
    return [
      "JobNumber",
      "Customer",
      "Product",
      "MainOperation",
      "planned_status",
      "planned_qty",
      "ActualProductionQty",
      "actual_waste_qty",
      "planned_setup_time",
      "planned_operational_time",
      "total_prod_time",
      "total_idle_time",
      "total_meal_time",
      "total_employees",
      "employee_list",
    ];
  }

  return [
    "JobNumber",
    "Customer",
    "Product",
    "MainOperation",
    "PlannedStatus",
    "WOQty",
    "OPQty",
    "PlannedQty",
    "ActualProductionQty",
    "ActualWasteQty",
    "PlanSetupTime",
    "OperationalTime",
    "TotalSetupTime",
    "TotalIdleTime",
    "TotalProductionTime",
    "TotalMealsTime",
    "duration",
    "IdleReasons",
    "JobCloseremarks",
  ];
};


// =======================================================
// FRONTEND TABLE STATUS CHIP + SUMMARY HELPERS
// =======================================================

const getPlanStatusStyle = (status, theme) => {
  const isDark = theme.palette.mode === "dark";

  const styles = {
    "Planned-Started": {
      label: "Planned Started",
      bg: alpha(theme.palette.success.main, isDark ? 0.22 : 0.12),
      color: isDark ? theme.palette.success.light : theme.palette.success.dark,
      border: alpha(theme.palette.success.main, isDark ? 0.7 : 0.55),
    },
    "Planned-NotStarted": {
      label: "Planned Not Started",
      bg: alpha(theme.palette.warning.main, isDark ? 0.22 : 0.14),
      color: isDark ? theme.palette.warning.light : theme.palette.warning.dark,
      border: alpha(theme.palette.warning.main, isDark ? 0.7 : 0.6),
    },
    "NonPlanned-Started": {
      label: "Non-Planned Started",
      bg: alpha(theme.palette.error.main, isDark ? 0.22 : 0.12),
      color: isDark ? theme.palette.error.light : theme.palette.error.dark,
      border: alpha(theme.palette.error.main, isDark ? 0.75 : 0.6),
    },
  };

  return (
    styles[status] || {
      label: status || "-",
      bg: alpha(theme.palette.text.secondary, isDark ? 0.16 : 0.08),
      color: theme.palette.text.primary,
      border: alpha(theme.palette.text.secondary, 0.35),
    }
  );
};

const isPlanStatusColumn = (columnName) => {
  return columnName === "PlannedStatus" || columnName === "planned_status";
};

const isTimeTotalColumn = (columnName) => {
  return [
    "TotalSetupTime",
    "TotalIdleTime",
    "TotalProductionTime",
    "TotalMealsTime",
    "duration",
    "total_duration",
    "total_prod_time",
    "total_idle_time",
    "total_meal_time",
  ].includes(columnName);
};

const isNumericTotalColumn = (columnName) => {
  return [
    "WOQty",
    "OPQty",
    "PlannedQty",
    "ActualInputQty",
    "ActualPassQty",
    "ActualProductionQty",
    "ActualWasteQty",
    "PlanSetupTime",
    "OperationalTime",
    "SetupAndOpeTime",
    "DurationHours",
    "TotalSetupHours",
    "TotalIdleHours",
    "TotalProductionHours",
    "TotalMealsHours",
    "planned_qty",
    "planned_wo_qty",
    "planned_op_qty",
    "actual_waste_qty",
    "planned_setup_time",
    "planned_operational_time",
    "total_employees",
  ].includes(columnName);
};

const convertTimeTextToHours = (value) => {
  if (!value) return 0;

  if (typeof value === "number") {
    return value;
  }

  const text = String(value);

  if (!text.includes(":")) {
    return Number(text) || 0;
  }

  const [hours = 0, minutes = 0, seconds = 0] = text.split(":").map(Number);

  return Number(
    ((hours || 0) + (minutes || 0) / 60 + (seconds || 0) / 3600).toFixed(2)
  );
};

const formatTotalValue = (value) => {
  const numberValue = Number(value || 0);

  if (Number.isInteger(numberValue)) {
    return numberValue.toLocaleString();
  }

  return numberValue.toFixed(2);
};

const getColumnTotal = (rows, columnName) => {
  if (!rows || rows.length === 0) return "";

  if (isTimeTotalColumn(columnName)) {
    const totalHours = rows.reduce((sum, row) => {
      return sum + convertTimeTextToHours(row[columnName]);
    }, 0);

    return totalHours > 0 ? `${totalHours.toFixed(2)} h` : "";
  }

  if (isNumericTotalColumn(columnName)) {
    const total = rows.reduce((sum, row) => {
      return sum + Number(row[columnName] || 0);
    }, 0);

    return total !== 0 ? formatTotalValue(total) : "";
  }

  return "";
};

const getAchievementText = (rows) => {
  if (!rows || rows.length === 0) return "No Data";

  const plannedQty = rows.reduce((sum, row) => {
    return sum + Number(row.PlannedQty || row.planned_qty || 0);
  }, 0);

  const actualQty = rows.reduce((sum, row) => {
    return sum + Number(row.ActualProductionQty || row.actual_prod_qty || 0);
  }, 0);

  if (plannedQty > 0) {
    return `${Math.round((actualQty / plannedQty) * 100)}%`;
  }

  if (plannedQty === 0 && actualQty > 0) {
    return `Unplanned: ${actualQty.toLocaleString()} Total`;
  }

  return "No Production";
};

const renderTableCellValue = (row, columnName, theme) => {
  const value = row[columnName];

  if (isPlanStatusColumn(columnName)) {
    const statusStyle = getPlanStatusStyle(value, theme);

    return (
      <Chip
        label={statusStyle.label}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 800,
          fontSize: "0.72rem",
          height: 24,
          backgroundColor: statusStyle.bg,
          color: statusStyle.color,
          borderColor: statusStyle.border,
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    );
  }

  return formatTableValue(value, columnName);
};

// =======================================================
// EXCEL HEADERS - MATCH OLD VIEW/DOWNLOAD FORMAT
// =======================================================

const filteredPlanHeaders = [
  "No:",
  "JobNumber",
  "Product",
  "Customer",
  "WOQty",
  "OPQty",
  "PlannedQty",
  "PlannedStatus",
  "ActualProductionQty",
  "MainOperation",
  "PlanSetupTime(h)",
  "OperationalTime(h)",
  "SetupAndOpeTime(h)",
  "TotalSetupTime(h)",
  "ActualRunningTime(h)",
  "TotalActualTime(h)",
  "TimeUnit",
  "WOduedate",
  "IdleReasons",
  "JobCloseRemarks",
];

const qcmPlanHeaders = [
  "No:",
  "JobNumber",
  "Product",
  "Customer",
  "WOQty",
  "OPQty",
  "PlannedQty",
  "PlannedStatus",
  "PassedQty",
  "MainOperation",
  "PlanSetupTime(h)",
  "OperationalTime(h)",
  "SetupAndOpeTime(h)",
  "TotalSetupTime(h)",
  "ActualRunningTime(h)",
  "TotalActualTime(h)",
  "TimeUnit",
  "WOduedate",
  "IdleReasons",
  "JobCloseRemarks",
];

const filteredShiftHeaders = [
  "No:",
  "shift_loggedIn_DateTime",
  "shift_loggedOut_DateTime",
  "Employees",
  "remarks",
];

const manHourPlanHeaders = [
  "No:",
  "JobNumber",
  "Product",
  "Customer",
  "MainOperation",
  "PlannedQty",
  "PlannedStatus",
  "ActualProductionQty",
  "PlannedSetupTime",
  "PlannedOperationalTime",
  "PlannedSetup+OperationalTime",
  "ActualSetupTime",
  "ActualRunningTime",
  "TotalProdTime",
  "TimeUnit",
  "EmployeeList",
];

const combinedEmployeeActivitiesHeaders = [
  "No:",
  "emp_id",
  "emp_name",
  "shift_in_datetime",
  "shift_out_datetime",
  "non_job_activities",
  "remarks",
];

// =======================================================
// EXCEL STYLE HELPERS
// =======================================================

const safeSheetName = (name) => String(name).replace(/[\\/*?:[\]]/g, "-").substring(0, 31);

const safeTableName = (name) => String(name).replace(/[^A-Za-z0-9_]/g, "_").substring(0, 200);

const applyBorder = (cell, color = "FF4682B4", bottomStyle = "thin") => {
  cell.border = {
    top: { style: "thin", color: { argb: color } },
    left: { style: "thin", color: { argb: color } },
    bottom: { style: bottomStyle, color: { argb: color } },
    right: { style: "thin", color: { argb: color } },
  };
};

const addMergedTitle = (sheet, rowNumber, text, fromCol, toCol, fillColor, fontSize = 14) => {
  sheet.addRow([text]);
  sheet.mergeCells(rowNumber, fromCol, rowNumber, toCol);

  const cell = sheet.getCell(rowNumber, fromCol);
  cell.font = { bold: true, size: fontSize, color: { argb: "FFFFFFFF" } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
  applyBorder(cell);
  sheet.getRow(rowNumber).height = 25;
};

const addSectionHeader = (sheet, rowNumber, text, fromCol, toCol) => {
  sheet.addRow([text]);
  sheet.mergeCells(rowNumber, fromCol, rowNumber, toCol);

  const cell = sheet.getCell(rowNumber, fromCol);
  cell.font = { bold: true, size: 11, color: { argb: "FF000000" } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
  applyBorder(cell);
};

const applyTableBorders = (sheet, startRow, rowCount, columnCount) => {
  for (let row = startRow; row <= startRow + rowCount; row += 1) {
    for (let col = 1; col <= columnCount; col += 1) {
      const cell = sheet.getCell(row, col);
      applyBorder(cell);
      if (row > startRow) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
      }
    }
  }
};

const optimizeColumns = (sheet, maxWidth = 30) => {
  sheet.columns.forEach((column) => {
    if (!column) return;

    let maxLength = 8;
    column.eachCell({ includeEmpty: false }, (cell) => {
      const text = cell.value ? String(cell.value) : "";
      maxLength = Math.max(maxLength, text.length);
    });

    column.width = Math.min(maxWidth, Math.max(8, maxLength + 2));
  });
};

const addExcelTable = (sheet, tableName, rowNumber, headers, rows) => {
  sheet.addTable({
    name: safeTableName(tableName),
    ref: `A${rowNumber}`,
    headerRow: true,
    style: { theme: "TableStyleMedium9", showRowStripes: true },
    columns: headers.map((header) => ({ name: header })),
    rows: rows.map((row) => headers.map((header) => row[header] ?? "")),
  });

  applyTableBorders(sheet, rowNumber, rows.length, headers.length);
};

// =======================================================
// FORMATTED MACHINE-HOUR EXCEL SHEET
// =======================================================

const generateMachineHourGroupedSheet = (workbook, groupCode, groupData, selectedDate) => {
  const sheet = workbook.addWorksheet(safeSheetName(`${groupCode}_PlanVsActual`));
  const fullOpelogSheet = workbook.addWorksheet(safeSheetName(`${groupCode}_FullOpeLog`));

  let currentRow = 1;
  const isQCMGroup = groupCode === "QCM";
  const dynamicPlanHeaders = isQCMGroup ? qcmPlanHeaders : filteredPlanHeaders;

  addMergedTitle(
    sheet,
    currentRow,
    `${groupCode} Group - Machine-Hour Plan Vs Actual - ${selectedDate.format("YYYY-MM-DD")}`,
    1,
    dynamicPlanHeaders.length,
    "FF4682B4",
    14
  );
  currentRow += 1;

  groupData.forEach(({ code, data, fullOpelog, nonJobData }, index) => {
    sheet.addRow([`📍 ${code}`]);
    sheet.mergeCells(currentRow, 1, currentRow, dynamicPlanHeaders.length);
    const stationTitleCell = sheet.getCell(currentRow, 1);
    stationTitleCell.font = { bold: true, size: 12, color: { argb: "FF000000" } };
    stationTitleCell.alignment = { horizontal: "left", vertical: "middle" };
    stationTitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB0C4DE" } };
    applyBorder(stationTitleCell);
    sheet.getRow(currentRow).height = 25;
    currentRow += 1;

    const stationData = data || {};

    // Shift Data
    if (stationData.shiftsData && stationData.shiftsData.length > 0) {
      addSectionHeader(sheet, currentRow, "📌 Shift Data", 1, filteredShiftHeaders.length);
      currentRow += 1;

      const processedShiftData = stationData.shiftsData.map((row, rowIndex) => ({
        "No:": rowIndex + 1,
        shift_loggedIn_DateTime: row.shift_loggedIn_DateTime
          ? dayjs(row.shift_loggedIn_DateTime).format("YYYY-MM-DD hh:mm A")
          : "",
        shift_loggedOut_DateTime: row.shift_loggedOut_DateTime
          ? dayjs(row.shift_loggedOut_DateTime).format("YYYY-MM-DD hh:mm A")
          : "",
        Employees: formatEmployees(row.operator, row.AstOperator || row.astOperator, row.Helpers || row.helpers),
        remarks: row.remarks || "",
      }));

      addExcelTable(
        sheet,
        `ShiftData_${code}_${index}`,
        currentRow,
        filteredShiftHeaders,
        processedShiftData
      );

      currentRow += processedShiftData.length + 1;
    }

    // Plan Vs Actual Data
    if (stationData.planData && stationData.planData.length > 0) {
      addSectionHeader(sheet, currentRow, "📌 Plan Vs Actual Data", 1, dynamicPlanHeaders.length);
      currentRow += 1;

      let totalPlannedQty = 0;
      let totalActualProductionQty = 0;
      let totalPlanSetupTimeHours = 0;
      let totalOperationalTimeHours = 0;
      let totalSetupAndOpeTimeHours = 0;
      let totalTotalSetupTimeHours = 0;
      let totalActualRunningTimeHours = 0;
      let totalTotalActualTimeHours = 0;

      const processedPlanData = stationData.planData.map((row, rowIndex) => {
        const planSetupTimeHours = Number(row.PlanSetupTime || 0);
        const operationalTimeHours = Number(row.OperationalTime || 0);
        const setupAndOpeTimeHours = Number(row.SetupAndOpeTime || 0);

        const totalSetupTimeSeconds = convertTimeToSeconds(row.TotalSetupTime);
        const totalSetupTimeHours = totalSetupTimeSeconds / 3600;

        const durationSeconds = convertTimeToSeconds(row.duration);
        const totalIdleTimeSeconds = convertTimeToSeconds(row.TotalIdleTime);
        const totalMealsTimeSeconds = convertTimeToSeconds(row.TotalMealsTime);

        const actualRunningTimeSeconds = Math.max(
          0,
          durationSeconds - totalSetupTimeSeconds - totalIdleTimeSeconds - totalMealsTimeSeconds
        );
        const totalActualTimeSeconds = Math.max(
          0,
          durationSeconds - totalIdleTimeSeconds - totalMealsTimeSeconds
        );

        const actualRunningTimeHours = actualRunningTimeSeconds / 3600;
        const totalActualTimeHours = totalActualTimeSeconds / 3600;

        totalPlanSetupTimeHours += planSetupTimeHours;
        totalOperationalTimeHours += operationalTimeHours;
        totalSetupAndOpeTimeHours += setupAndOpeTimeHours;
        totalTotalSetupTimeHours += totalSetupTimeHours;
        totalActualRunningTimeHours += actualRunningTimeHours;
        totalTotalActualTimeHours += totalActualTimeHours;

        const plannedQty = Number(row.PlannedQty || row.planned_qty || 0);
        const actualQty = isQCMGroup
          ? Number(row.PassedQty || row.ActualPassQty || 0)
          : Number(row.ActualProductionQty || 0);

        totalPlannedQty += plannedQty;
        totalActualProductionQty += actualQty;

        const returnObject = {
          "No:": rowIndex + 1,
          JobNumber: row.JobNumber || row.job_number || "",
          Product: row.Product || row.product || "",
          Customer: row.Customer || row.customer || "",
          WOQty: row.WOQty || row.planned_wo_qty || 0,
          OPQty: row.OPQty || row.planned_op_qty || 0,
          PlannedQty: plannedQty,
          PlannedStatus: row.PlannedStatus || row.planned_status || "",
          MainOperation: row.MainOperation || row.main_operation || "",
          "PlanSetupTime(h)": planSetupTimeHours,
          "OperationalTime(h)": operationalTimeHours,
          "SetupAndOpeTime(h)": setupAndOpeTimeHours,
          "TotalSetupTime(h)": roundSmallValues(totalSetupTimeHours),
          "ActualRunningTime(h)": roundSmallValues(actualRunningTimeHours),
          "TotalActualTime(h)": roundSmallValues(totalActualTimeHours),
          TimeUnit: "hrs",
          WOduedate: row.WOduedate || row.planned_due_date || "",
          IdleReasons: row.IdleReasons || "",
          JobCloseRemarks: row.JobCloseremarks || row.job_close_remarks || "",
        };

        if (isQCMGroup) {
          returnObject.PassedQty = actualQty;
        } else {
          returnObject.ActualProductionQty = actualQty;
        }

        return returnObject;
      });

      addExcelTable(
        sheet,
        `PlanData_${code}_${index}`,
        currentRow,
        dynamicPlanHeaders,
        processedPlanData
      );

      currentRow += processedPlanData.length + 1;

      const totalRowValues = new Array(dynamicPlanHeaders.length).fill("");
      totalRowValues[1] = "TOTAL";
      totalRowValues[6] = totalPlannedQty;
      totalRowValues[8] = totalActualProductionQty;
      totalRowValues[10] = Number(totalPlanSetupTimeHours.toFixed(2));
      totalRowValues[11] = Number(totalOperationalTimeHours.toFixed(2));
      totalRowValues[12] = Number(totalSetupAndOpeTimeHours.toFixed(2));
      totalRowValues[13] = roundSmallValues(totalTotalSetupTimeHours);
      totalRowValues[14] = roundSmallValues(totalActualRunningTimeHours);
      totalRowValues[15] = roundSmallValues(totalTotalActualTimeHours);

      sheet.addRow(totalRowValues);
      for (let col = 1; col <= dynamicPlanHeaders.length; col += 1) {
        const cell = sheet.getCell(currentRow, col);
        const value = totalRowValues[col - 1];
        if (value !== "" && value !== null && value !== undefined) {
          cell.font = { bold: true, color: { argb: "FF000000" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          applyBorder(cell, "FF4682B4", [7, 9, 13, 16].includes(col) ? "double" : "thin");
        }
      }
      currentRow += 1;

      sheet.addRow([]);
      currentRow += 1;

      const achievementText =
        totalPlannedQty > 0
          ? `Overall Achievement = ${Math.round((totalActualProductionQty / totalPlannedQty) * 100)}%`
          : totalActualProductionQty > 0
            ? `Overall Achievement = Unplanned: ${totalActualProductionQty} Total`
            : "Overall Achievement = No Production";

      sheet.mergeCells(currentRow, 9, currentRow, 10);
      const achievementCell = sheet.getCell(currentRow, 9);
      achievementCell.value = achievementText;
      achievementCell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      achievementCell.alignment = { horizontal: "center", vertical: "middle" };
      achievementCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E7D32" } };
      applyBorder(achievementCell, "FF2E7D32");
      sheet.getRow(currentRow).height = 25;
      currentRow += 1;
    }

    // Non-job data section
    if (nonJobData && nonJobData.length > 0) {
      addSectionHeader(sheet, currentRow, "📌 Non-Job Related Data", 1, 2);
      currentRow += 1;

      const nonJobHeaders = ["No:", "Operation Log Entry"];
      const processedNonJobData = nonJobData.map((row, rowIndex) => {
        const startTime = row.start_datetime ? dayjs(row.start_datetime).format("h.mmA") : "";
        const endTime = row.end_datetime ? dayjs(row.end_datetime).format("h.mmA") : "";
        const durationMinutes = Math.round(Number(row.duration_hours || 0) * 60);

        return {
          "No:": rowIndex + 1,
          "Operation Log Entry": `*${row.operation_description || ""} = ${startTime} - ${endTime} = ${durationMinutes}min`,
        };
      });

      addExcelTable(sheet, `NonJobData_${code}_${index}`, currentRow, nonJobHeaders, processedNonJobData);
      sheet.getColumn(2).width = 60;
      currentRow += processedNonJobData.length + 1;
      sheet.addRow([]);
      currentRow += 1;
    }

    // Full operation log sheet
    if (fullOpelog && fullOpelog.length > 0) {
      fullOpelogSheet.addRow([`${code} - Full Operation Log`]);
      const sectionHeader = fullOpelogSheet.lastRow;
      sectionHeader.font = { bold: true, size: 11 };
      sectionHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB0C4DE" } };

      fullOpelogSheet.addRow([
        "operation_code",
        "operation_description",
        "start_datetime",
        "end_datetime",
        "remarks",
        "duration_hours",
      ]);
      const headerRow = fullOpelogSheet.lastRow;
      headerRow.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4682B4" } };

      const fullOpelogStartRow = fullOpelogSheet.lastRow.number;
      fullOpelog.forEach((item) => {
        fullOpelogSheet.addRow([
          item.operation_code || "",
          item.operation_description || "",
          item.start_datetime ? dayjs(item.start_datetime).format("YYYY-MM-DD hh:mm A") : "",
          item.end_datetime ? dayjs(item.end_datetime).format("YYYY-MM-DD hh:mm A") : "",
          item.remarks || "",
          item.duration_hours || "",
        ]);
      });

      for (let row = fullOpelogStartRow; row <= fullOpelogSheet.lastRow.number; row += 1) {
        for (let col = 1; col <= 6; col += 1) {
          const cell = fullOpelogSheet.getCell(row, col);
          applyBorder(cell);
          if (row > fullOpelogStartRow) {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
          }
        }
      }
    }

    if (index < groupData.length - 1) {
      sheet.addRow([]);
      currentRow += 1;
      sheet.addRow([]);
      currentRow += 1;
    }
  });

  optimizeColumns(sheet, 30);
  optimizeColumns(fullOpelogSheet, 30);
};

// =======================================================
// FORMATTED MAN-HOUR EXCEL SHEET
// =======================================================

const generateManHourGroupedSheet = (workbook, groupCode, groupData, selectedDate) => {
  const sheet = workbook.addWorksheet(safeSheetName(`${groupCode}_ManHour_PlanVsActual`));
  let currentRow = 1;

  addMergedTitle(
    sheet,
    currentRow,
    `${groupCode} Group - Man-Hour Plan Vs Actual - ${selectedDate.format("YYYY-MM-DD")}`,
    1,
    manHourPlanHeaders.length,
    "FF4682B4",
    16
  );
  currentRow += 1;
  sheet.addRow([]);
  currentRow += 1;

  groupData.forEach(({ code, data }, index) => {
    sheet.addRow([`📍 ${code}`]);
    sheet.mergeCells(currentRow, 1, currentRow, manHourPlanHeaders.length);
    const stationTitleCell = sheet.getCell(currentRow, 1);
    stationTitleCell.font = { bold: true, size: 14, color: { argb: "FF000000" } };
    stationTitleCell.alignment = { horizontal: "left", vertical: "middle" };
    stationTitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB0C4DE" } };
    applyBorder(stationTitleCell);
    sheet.getRow(currentRow).height = 25;
    currentRow += 1;

    const stationData = data || {};
    const combinedEmployeeData = processEmployeeActivitiesData(stationData.employeeActivitiesData || []);

    if (combinedEmployeeData.length > 0) {
      addSectionHeader(sheet, currentRow, "📌 Employee Activities & Shifts", 1, combinedEmployeeActivitiesHeaders.length);
      currentRow += 1;

      const tableStartRow = currentRow;
      addExcelTable(
        sheet,
        `EmployeeActivities_${code}_${index}`,
        currentRow,
        combinedEmployeeActivitiesHeaders,
        combinedEmployeeData
      );

      for (let row = tableStartRow; row <= tableStartRow + combinedEmployeeData.length; row += 1) {
        const activityCell = sheet.getCell(row, 6);
        activityCell.alignment = { horizontal: "left", vertical: "top", wrapText: true, indent: 1 };

        if (row > tableStartRow && activityCell.value && String(activityCell.value).includes("\n")) {
          const lineCount = (String(activityCell.value).match(/\n/g) || []).length + 1;
          sheet.getRow(row).height = Math.max(20, lineCount * 18);
        }
      }
      sheet.getColumn(6).width = 50;

      currentRow += combinedEmployeeData.length + 1;
      sheet.addRow([]);
      currentRow += 1;
    }

    if (stationData.planData && stationData.planData.length > 0) {
      addSectionHeader(sheet, currentRow, "📌 Man-Hour Plan Vs Actual Data", 1, manHourPlanHeaders.length);
      currentRow += 1;

      let totalPlannedQty = 0;
      let totalActualProductionQty = 0;
      let totalPlannedSetupTime = 0;
      let totalPlannedOperationalTime = 0;
      let totalPlannedSetupOperationalTime = 0;
      let totalActualSetupTime = 0;
      let totalActualRunningTime = 0;
      let totalTotalProdTime = 0;

      const processedPlanData = stationData.planData.map((row, rowIndex) => {
        const plannedQty = Number(row.PlannedQty || row.planned_qty || 0);
        const actualQty = Number(row.ActualProductionQty || row.actual_prod_qty || 0);
        const plannedSetupTime = Number(row.planned_setup_time || 0);
        const plannedOperationalTime = Number(row.planned_operational_time || 0);
        const plannedSetupOperationalTime = plannedSetupTime + plannedOperationalTime;
        const totalProdTimeDecimal = convertTimeToDecimalHours(row.TotalProdTime || row.total_prod_time || 0);
        const actualSetupTime = 0;
        const actualRunningTime = totalProdTimeDecimal;

        totalPlannedQty += plannedQty;
        totalActualProductionQty += actualQty;
        totalPlannedSetupTime += plannedSetupTime;
        totalPlannedOperationalTime += plannedOperationalTime;
        totalPlannedSetupOperationalTime += plannedSetupOperationalTime;
        totalActualSetupTime += actualSetupTime;
        totalActualRunningTime += actualRunningTime;
        totalTotalProdTime += totalProdTimeDecimal;

        return {
          "No:": rowIndex + 1,
          JobNumber: row.JobNumber || row.job_number || "",
          Product: row.Product || row.product || "",
          Customer: row.Customer || row.customer || "",
          MainOperation: row.MainOperation || row.main_operation || "",
          PlannedQty: plannedQty,
          PlannedStatus: row.PlannedStatus || row.planned_status || "",
          ActualProductionQty: actualQty,
          PlannedSetupTime: formatDecimalHours(plannedSetupTime),
          PlannedOperationalTime: formatDecimalHours(plannedOperationalTime),
          "PlannedSetup+OperationalTime": formatDecimalHours(plannedSetupOperationalTime),
          ActualSetupTime: formatDecimalHours(actualSetupTime),
          ActualRunningTime: formatDecimalHours(actualRunningTime),
          TotalProdTime: formatDecimalHours(totalProdTimeDecimal),
          TimeUnit: "Man hrs",
          EmployeeList: row.EmployeeList || row.employee_list || "",
        };
      });

      addExcelTable(
        sheet,
        `ManHourPlanData_${code}_${index}`,
        currentRow,
        manHourPlanHeaders,
        processedPlanData
      );

      currentRow += processedPlanData.length + 1;

      const totalRowValues = new Array(manHourPlanHeaders.length).fill("");
      totalRowValues[1] = "TOTAL";
      totalRowValues[5] = totalPlannedQty;
      totalRowValues[7] = totalActualProductionQty;
      totalRowValues[8] = formatDecimalHours(totalPlannedSetupTime);
      totalRowValues[9] = formatDecimalHours(totalPlannedOperationalTime);
      totalRowValues[10] = formatDecimalHours(totalPlannedSetupOperationalTime);
      totalRowValues[11] = formatDecimalHours(totalActualSetupTime);
      totalRowValues[12] = formatDecimalHours(totalActualRunningTime);
      totalRowValues[13] = formatDecimalHours(totalTotalProdTime);

      sheet.addRow(totalRowValues);
      const totalRow = sheet.getRow(currentRow);
      totalRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      totalRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4682B4" } };
      totalRow.alignment = { horizontal: "center", vertical: "middle" };

      for (let col = 1; col <= manHourPlanHeaders.length; col += 1) {
        const cell = sheet.getCell(currentRow, col);
        const value = totalRowValues[col - 1];
        if (value !== "" && value !== null && value !== undefined) {
          applyBorder(cell, "FF4682B4", "double");
        }
      }
      currentRow += 1;

      sheet.addRow([]);
      currentRow += 1;

      const achievementText =
        totalPlannedQty > 0
          ? `Overall Achievement = ${Math.round((totalActualProductionQty / totalPlannedQty) * 100)}%`
          : totalActualProductionQty > 0
            ? `Overall Achievement = Unplanned: ${totalActualProductionQty} Total`
            : "Overall Achievement = No Production";

      sheet.mergeCells(currentRow, 7, currentRow, 8);
      const achievementCell = sheet.getCell(currentRow, 7);
      achievementCell.value = achievementText;
      achievementCell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      achievementCell.alignment = { horizontal: "center", vertical: "middle" };
      achievementCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E7D32" } };
      applyBorder(achievementCell, "FF2E7D32");
      sheet.getRow(currentRow).height = 25;
      currentRow += 1;
    }

    if (index < groupData.length - 1) {
      sheet.addRow([]);
      currentRow += 1;
      sheet.addRow([]);
      currentRow += 1;
    }
  });

  optimizeColumns(sheet, 32);
  sheet.getColumn(6).width = Math.max(sheet.getColumn(6).width || 0, 50);
};

// =======================================================
// SCREEN COMPONENTS
// =======================================================

function SummaryCards({ reportData }) {
  const summary = useMemo(() => {
    let totalJobs = 0;
    let totalPlannedQty = 0;
    let totalActualQty = 0;
    const machineCount = Object.keys(reportData || {}).length;

    Object.values(reportData || {}).forEach((machine) => {
      const rows = machine.planData || [];
      totalJobs += rows.length;

      rows.forEach((row) => {
        totalPlannedQty += toNumber(row.PlannedQty || row.planned_qty);
        totalActualQty += toNumber(row.ActualProductionQty || row.actual_prod_qty);
      });
    });

    const achievement = totalPlannedQty > 0 ? Math.round((totalActualQty / totalPlannedQty) * 100) : 0;

    return {
      machineCount,
      totalJobs,
      totalPlannedQty,
      totalActualQty,
      achievement,
    };
  }, [reportData]);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Workstations
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {summary.machineCount}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Jobs / Operations
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {summary.totalJobs}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Actual Qty
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {summary.totalActualQty.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Achievement
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {summary.achievement}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function SimpleDataTable({
  title,
  rows,
  columns,
  emptyText,
  showPlanSummary = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const achievementText = showPlanSummary ? getAchievementText(rows) : "";

  const headerBg = isDark
    ? alpha(theme.palette.primary.main, 0.22)
    : alpha(theme.palette.primary.main, 0.12);

  const headerColor = isDark
    ? theme.palette.primary.light
    : theme.palette.primary.dark;

  const totalRowBg = isDark
    ? alpha(theme.palette.primary.main, 0.16)
    : alpha(theme.palette.primary.main, 0.07);

  const borderColor = isDark
    ? alpha(theme.palette.divider, 0.8)
    : theme.palette.divider;

  const achievementBg = isDark
    ? alpha(theme.palette.success.main, 0.28)
    : theme.palette.success.dark;

  const achievementColor = isDark
    ? theme.palette.success.light
    : theme.palette.common.white;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          {title}
        </Typography>

        {!rows || rows.length === 0 ? (
          <Alert severity="info">{emptyText || "No records found."}</Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: 520,
              borderRadius: 2,
              border: `1px solid ${borderColor}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: headerBg,
                      color: headerColor,
                      whiteSpace: "nowrap",
                      borderColor,
                    }}
                  >
                    #
                  </TableCell>

                  {columns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontWeight: 900,
                        backgroundColor: headerBg,
                        color: headerColor,
                        whiteSpace: "nowrap",
                        borderColor,
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.id || row.ID || index}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          isDark ? 0.12 : 0.05
                        ),
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        borderColor,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    {columns.map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          minWidth: isPlanStatusColumn(col) ? 170 : 120,
                          borderColor,
                          color: theme.palette.text.primary,
                          whiteSpace:
                            col === "IdleReasons" ||
                            col === "JobCloseremarks" ||
                            col === "employee_list" ||
                            col === "non_job_activities"
                              ? "normal"
                              : "nowrap",
                        }}
                      >
                        {renderTableCellValue(row, col, theme)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {showPlanSummary && (
                  <>
                    <TableRow
                      sx={{
                        backgroundColor: totalRowBg,
                        "& td": {
                          borderTop: `2px solid ${theme.palette.primary.main}`,
                          borderColor,
                          fontWeight: 900,
                          color: headerColor,
                        },
                      }}
                    >
                      <TableCell>TOTAL</TableCell>

                      {columns.map((col) => {
                        const totalValue = getColumnTotal(rows, col);

                        return (
                          <TableCell key={col}>
                            {col === "JobNumber" || col === "job_number"
                              ? "TOTAL"
                              : totalValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        sx={{
                          backgroundColor: achievementBg,
                          color: achievementColor,
                          fontWeight: 900,
                          textAlign: "center",
                          fontSize: "0.95rem",
                          py: 1.2,
                          borderColor,
                        }}
                      >
                        Overall Achievement = {achievementText}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default function MachineSupervisorPlanVsActualReport({ supervisorId }) {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [availableWorkstations, setAvailableWorkstations] = useState([]);
  const [selectedWorkstations, setSelectedWorkstations] = useState({});
  const [reportData, setReportData] = useState({});
  const [activeMachine, setActiveMachine] = useState("");
  const [alert, setAlert] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const selectedList = useMemo(() => {
    return availableWorkstations.filter((workstation) => selectedWorkstations[workstation.code]);
  }, [availableWorkstations, selectedWorkstations]);

  const activeData = activeMachine ? reportData[activeMachine] : null;
  const activeHoursType = activeData?.hoursType;

  const showAlert = (severity, message) => {
    setAlert({ open: true, severity, message });
  };

  const fetchAvailableWorkstations = async () => {
    if (!supervisorId) {
      showAlert("error", "Supervisor ID is missing.");
      return;
    }

    try {
      setLoading(true);
      setReportData({});
      setActiveMachine("");

      const response = await axios.get(`${API_BASE_URL}/api/machinesupervisor/planvsactual/available`, {
        params: {
          supervisorId,
          selectedDate: selectedDate.format("YYYY-MM-DD"),
        },
      });

      const rows = response.data.workstations || [];
      const initialSelection = {};
      rows.forEach((row) => {
        initialSelection[row.code] = true;
      });

      setAvailableWorkstations(rows);
      setSelectedWorkstations(initialSelection);

      if (rows.length === 0) {
        showAlert("info", "No Plan Vs Actual report data found for your allocated groups.");
      } else {
        showAlert("success", `${rows.length} workstation(s) found.`);
      }
    } catch (error) {
      console.error(error);
      showAlert("error", error.response?.data?.error || "Failed to fetch available workstations.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async () => {
    if (selectedList.length === 0) {
      showAlert("warning", "Please select at least one workstation.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        supervisorId,
        selectedDate: selectedDate.format("YYYY-MM-DD"),
        workstations: selectedList.map((workstation) => ({
          code: workstation.code,
          hoursType: workstation.HoursType,
        })),
      };

      const response = await axios.post(`${API_BASE_URL}/api/machinesupervisor/planvsactual/data`, payload);
      const data = response.data.data || {};

      setReportData(data);
      setActiveMachine(Object.keys(data)[0] || "");
      showAlert("success", "Report loaded successfully.");
    } catch (error) {
      console.error(error);
      showAlert("error", error.response?.data?.error || "Failed to load report data.");
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkstation = (code) => {
    setSelectedWorkstations((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const handleDownload = async () => {
    if (!reportData || Object.keys(reportData).length === 0) {
      showAlert("warning", "Please load report data before downloading.");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "SC Tracking App";

      const machineHourByGroup = {};
      const manHourByGroup = {};

      Object.entries(reportData).forEach(([machineCode, machineData]) => {
        const groupCode = machineData.groupCode || machineData.MachineGroupCode || "OTHER";
        const isManHour = isManHourType(machineData.hoursType);

        if (isManHour) {
          if (!manHourByGroup[groupCode]) manHourByGroup[groupCode] = [];

          manHourByGroup[groupCode].push({
            code: machineCode,
            data: {
              planData: machineData.planData || [],
              employeeActivitiesData: machineData.employeeActivitiesData || [],
              shiftsData: machineData.shiftsData || [],
              opelogData: machineData.opelogData || [],
            },
          });
        } else {
          if (!machineHourByGroup[groupCode]) machineHourByGroup[groupCode] = [];

          machineHourByGroup[groupCode].push({
            code: machineCode,
            data: {
              planData: machineData.planData || [],
              shiftsData: machineData.shiftsData || [],
              opelogData: machineData.opelogData || [],
            },
            fullOpelog: machineData.opelogData || [],
            nonJobData: machineData.opelogData || [],
          });
        }
      });

      Object.entries(machineHourByGroup).forEach(([groupCode, groupData]) => {
        generateMachineHourGroupedSheet(workbook, groupCode, groupData, selectedDate);
      });

      Object.entries(manHourByGroup).forEach(([groupCode, groupData]) => {
        generateManHourGroupedSheet(workbook, groupCode, groupData, selectedDate);
      });

      const fileName = `Supervisor_PlanVsActual_${selectedDate.format("DDMMMMYYYY")}_${dayjs().format("hhmmssA")}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, fileName);
      showAlert("success", `Excel file "${fileName}" downloaded successfully.`);
    } catch (error) {
      console.error("Supervisor formatted Excel download error:", error);
      showAlert("error", "Failed to download formatted Excel file.");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%" }}>
        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AssessmentIcon color="primary" />
              <Box>
                <Typography variant="h5" fontWeight={900}>
                  Plan Vs Actual Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and download Plan Vs Actual reports for allocated workstation groups.
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Report Date"
                  value={selectedDate}
                  onChange={(value) => setSelectedDate(value || dayjs())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} /> : <SearchIcon />}
                  onClick={fetchAvailableWorkstations}
                  disabled={loading}
                  sx={{ height: 40, textTransform: "none", fontWeight: 800 }}
                >
                  Show Workstations
                </Button>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={fetchReportData}
                  disabled={loading || availableWorkstations.length === 0}
                  sx={{ height: 40, textTransform: "none", fontWeight: 800 }}
                >
                  View Report
                </Button>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  color="success"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  disabled={Object.keys(reportData).length === 0}
                  sx={{ height: 40, textTransform: "none", fontWeight: 800 }}
                >
                  Download Excel
                </Button>
              </Grid>
            </Grid>

            {availableWorkstations.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                  Available Workstations
                </Typography>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {availableWorkstations.map((item) => {
                    const checked = !!selectedWorkstations[item.code];
                    const isMan = isManHourType(item.HoursType);

                    return (
                      <Chip
                        key={item.code}
                        icon={<Checkbox checked={checked} size="small" sx={{ p: 0 }} />}
                        label={`${item.code} | ${item.MachineGroupCode} | ${isMan ? "Man-Hour" : "Machine-Hour"}`}
                        color={checked ? (isMan ? "secondary" : "primary") : "default"}
                        variant={checked ? "filled" : "outlined"}
                        onClick={() => toggleWorkstation(item.code)}
                        sx={{ fontWeight: 700 }}
                      />
                    );
                  })}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        {Object.keys(reportData).length > 0 && <SummaryCards reportData={reportData} />}

        {Object.keys(reportData).length > 0 && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Tabs
                value={activeMachine}
                onChange={(event, value) => setActiveMachine(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {Object.entries(reportData).map(([machineCode, item]) => (
                  <Tab
                    key={machineCode}
                    value={machineCode}
                    label={`${machineCode} (${isManHourType(item.hoursType) ? "Man" : "Machine"})`}
                  />
                ))}
              </Tabs>
            </CardContent>
          </Card>
        )}

        {activeData && (
          <>
            <SimpleDataTable
              title={`${activeMachine} - Plan Vs Actual Data`}
              rows={activeData.planData || []}
              columns={getPlanColumns(activeHoursType)}
              emptyText="No plan vs actual records found for this workstation."
              showPlanSummary
            />

            {isManHourType(activeHoursType) ? (
              <SimpleDataTable
                title={`${activeMachine} - Employee Activities`}
                rows={activeData.employeeActivitiesData || []}
                columns={[
                  "emp_id",
                  "emp_name",
                  "shift_in_datetime",
                  "shift_out_datetime",
                  "non_job_activities",
                  "shift_remarks",
                  "activity_count",
                ]}
                emptyText="No employee activity data found."
              />
            ) : (
              <>
                <SimpleDataTable
                  title={`${activeMachine} - Shift Data`}
                  rows={activeData.shiftsData || []}
                  columns={[
                    "shift_loggedIn_DateTime",
                    "shift_loggedOut_DateTime",
                    "operator",
                    "AstOperator",
                    "Helpers",
                    "remarks",
                    "ActiveStatus",
                  ]}
                  emptyText="No shift data found."
                />

                <SimpleDataTable
                  title={`${activeMachine} - Operation Log / Non-Job Data`}
                  rows={activeData.opelogData || []}
                  columns={[
                    "operation_code",
                    "operation_description",
                    "start_datetime",
                    "end_datetime",
                    "duration_hours",
                    "remarks",
                    "active_status",
                  ]}
                  emptyText="No operation log data found."
                />
              </>
            )}
          </>
        )}

        <Snackbar
          open={alert.open}
          autoHideDuration={5000}
          onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={alert.severity} onClose={() => setAlert((prev) => ({ ...prev, open: false }))}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
